import { MapPin, Home, BedDouble, Bath, Car, Ruler, ChevronLeft, ChevronRight, X, Share2, MessageCircle, Send, Check, Calendar } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useState, useEffect } from "react";

const ModernaTemplate = ({ property, formData, setFormData, handleSubmit, submitting, openWhatsApp, handleShare, isDraftPreview, getImageUrl }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  
  const primaryColor = property.primary_color || "#a033fd";
  const secondaryColor = property.secondary_color || "#ff1758";
  const allImages = [property.main_image, ...(property.images || [])].filter(Boolean);
  
  // Preload all images so carousel is instant
  useEffect(() => {
    allImages.forEach((src) => {
      const img = new Image();
      img.src = getImageUrl(src, { width: 1200 });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(price);
  };

  const extraSpaces = [
    property.specs?.tv_room && "Estar de TV",
    property.specs?.laundry_room && "Cuarto de Lavado",
    property.specs?.service_room && "Cuarto de Servicio",
    property.specs?.roof_garden && "Roof Garden",
    property.specs?.balcony && "Balcón",
    property.specs?.terrace && "Terraza",
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" style={{ '--primary': primaryColor, '--secondary': secondaryColor }}>
      {/* Draft Banner */}
      {isDraftPreview && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium shadow-lg">
          Vista previa — Esta propiedad no es visible al público.{" "}
          <a href="/precios" className="underline font-bold">Activa tu plan para publicarla</a>
        </div>
      )}
      
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">{property.title}</h1>
            <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1 truncate">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{property.location}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={handleShare} className="hidden sm:flex">
              <Share2 className="w-4 h-4 mr-1" /> Compartir
            </Button>
            {property.contact_method !== "form" && (
              <Button size="sm" onClick={openWhatsApp} className="text-white" style={{ background: primaryColor }}>
                <MessageCircle className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">WhatsApp</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Split Layout: Gallery + Info */}
      <div className="max-w-7xl mx-auto w-full">
        <div className="flex flex-col lg:grid lg:grid-cols-5 gap-0 lg:gap-8 lg:p-6 w-full">
          
          {/* Left: Gallery - 3 cols, mobile order 1 */}
          <div className="lg:col-span-3 lg:row-start-1 w-full min-w-0 order-1">
            {/* Main Image */}
            <div className="relative aspect-[4/3] lg:rounded-2xl overflow-hidden cursor-pointer group w-full" onClick={() => setShowGallery(true)}>
              {allImages.map((src, i) => (
                <img
                  key={i}
                  src={getImageUrl(src, { width: 1200 })}
                  alt={property.title}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 group-hover:scale-105 ${currentImage === i ? 'opacity-100' : 'opacity-0'}`}
                  loading={i === 0 ? "eager" : "lazy"}
                />
              ))}
              <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1.5 rounded-full text-sm backdrop-blur-sm z-10">
                {currentImage + 1} / {allImages.length}
              </div>
              {/* Nav arrows */}
              {allImages.length > 1 && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); setCurrentImage(i => (i - 1 + allImages.length) % allImages.length); }} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors shadow-lg z-10">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setCurrentImage(i => (i + 1) % allImages.length); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors shadow-lg z-10">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnail Strip */}
            {allImages.length > 1 && (
              <div className="flex gap-2 p-4 lg:px-0 lg:pt-4 overflow-x-auto">
                {allImages.slice(0, 6).map((img, i) => (
                  <button key={i} onClick={() => setCurrentImage(i)}
                    className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${currentImage === i ? 'border-gray-900 shadow-md' : 'border-transparent opacity-70 hover:opacity-100'}`}>
                    <img src={getImageUrl(img, { width: 200 })} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
                {allImages.length > 6 && (
                  <button onClick={() => setShowGallery(true)} className="flex-shrink-0 w-20 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600 hover:bg-gray-200">
                    +{allImages.length - 6}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Video + Map: under gallery on desktop (col-span-3 row 2), order-3 on mobile (after right column) */}
          {(property.video_embed_url || (property.latitude && property.longitude)) && (
            <div className="lg:col-span-3 lg:row-start-2 px-4 lg:px-0 space-y-6 mt-6 lg:mt-0 order-3">
              {property.video_embed_url && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Video</h3>
                  <div className="aspect-video rounded-2xl overflow-hidden shadow-lg">
                    <iframe src={property.video_embed_url} title="Video" className="w-full h-full" allowFullScreen />
                  </div>
                </div>
              )}
              {property.latitude && property.longitude && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Ubicación</h3>
                  <div className="h-[320px] rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                    <iframe
                      title="Ubicación"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${property.longitude-0.005},${property.latitude-0.005},${property.longitude+0.005},${property.latitude+0.005}&layer=mapnik&marker=${property.latitude},${property.longitude}`}
                      className="w-full h-full border-0"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Right: Info + Form - 2 cols (sticky on desktop, mobile order 2 = price/specs/desc/amenities only) */}
          <div className="lg:col-span-2 lg:row-start-1 lg:row-span-2 p-4 lg:p-0 w-full min-w-0 order-2">
            <div className="lg:sticky lg:top-20">
            {/* Price */}
            <div className="mb-6">
              <p className="text-4xl font-bold" style={{ color: primaryColor }}>{formatPrice(property.price)}</p>
              <p className="text-gray-500 text-sm mt-1">{property.operation_type === "renta" ? "Renta mensual" : "Precio de venta"}</p>
            </div>

            {/* Specs Grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {property.specs?.bedrooms > 0 && (
                <div className="flex items-center gap-2.5 bg-gray-50 rounded-xl p-3">
                  <BedDouble className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-semibold text-sm">{property.specs.bedrooms}</p>
                    <p className="text-xs text-gray-500">Recámaras</p>
                  </div>
                </div>
              )}
              {property.specs?.bathrooms > 0 && (
                <div className="flex items-center gap-2.5 bg-gray-50 rounded-xl p-3">
                  <Bath className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-semibold text-sm">{property.specs.bathrooms}</p>
                    <p className="text-xs text-gray-500">Baños</p>
                  </div>
                </div>
              )}
              {property.specs?.parking > 0 && (
                <div className="flex items-center gap-2.5 bg-gray-50 rounded-xl p-3">
                  <Car className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-semibold text-sm">{property.specs.parking}</p>
                    <p className="text-xs text-gray-500">Estacionamiento</p>
                  </div>
                </div>
              )}
              {property.specs?.half_bathrooms > 0 && (
                <div className="flex items-center gap-2.5 bg-gray-50 rounded-xl p-3">
                  <Bath className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-semibold text-sm">{property.specs.half_bathrooms}</p>
                    <p className="text-xs text-gray-500">Medio Baño</p>
                  </div>
                </div>
              )}
              {property.specs?.construction_meters > 0 && (
                <div className="flex items-center gap-2.5 bg-gray-50 rounded-xl p-3">
                  <Ruler className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-semibold text-sm">{property.specs.construction_meters}m²</p>
                    <p className="text-xs text-gray-500">Construcción</p>
                  </div>
                </div>
              )}
              {property.specs?.square_meters > 0 && (
                <div className="flex items-center gap-2.5 bg-gray-50 rounded-xl p-3">
                  <Ruler className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-semibold text-sm">{property.specs.square_meters}m²</p>
                    <p className="text-xs text-gray-500">Terreno</p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="font-bold text-gray-900 mb-2">Descripción</h3>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{property.description}</p>
            </div>

            {/* Amenities */}
            {property.amenities && property.amenities.length > 0 && (
              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Amenidades</h3>
                <div className="flex flex-wrap gap-2">
                  {property.amenities.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-gray-50 text-gray-700">
                      <Check className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                      {a.label || a.name || a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Espacios Adicionales */}
            {extraSpaces.length > 0 && (
              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Espacios Adicionales</h3>
                <div className="flex flex-wrap gap-2">
                  {extraSpaces.map((space, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-gray-50 text-gray-700">
                      <Check className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                      {space}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Form (desktop only — mobile shows it at bottom) */}
            {property.contact_method !== "whatsapp" && (
              <div id="contact-form" className="hidden lg:block rounded-2xl p-5 border border-gray-200 bg-gray-50 scroll-mt-24">
                <h3 className="font-bold text-gray-900 mb-1">{property.form_title || "¿Te interesa?"}</h3>
                <p className="text-sm text-gray-500 mb-4">{property.form_description || "Déjanos tus datos y te contactamos"}</p>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <Input
                    placeholder="Tu nombre"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="tel"
                      placeholder="WhatsApp"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  <Input
                    placeholder="Mensaje (opcional)"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  />
                  <Button type="submit" className="w-full text-white" style={{ background: primaryColor }} disabled={submitting}>
                    {submitting ? "Enviando..." : "Enviar Mensaje"}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </div>
            )}

            {/* WhatsApp-only CTA (desktop only — mobile shows it at bottom) */}
            {property.contact_method === "whatsapp" && (
              <div id="contact-form" className="hidden lg:block rounded-2xl p-6 text-white scroll-mt-24" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-lg">{property.form_title || "Agenda una visita"}</h3>
                      <p className="text-sm text-white/85 mt-0.5">{property.form_description || "Conoce esta propiedad en persona. Te respondemos al instante."}</p>
                    </div>
                </div>
                <ul className="space-y-1.5 mb-5 text-sm text-white/90">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 flex-shrink-0" /> Respuesta en minutos</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 flex-shrink-0" /> Sin compromiso</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 flex-shrink-0" /> Resolvemos tus dudas</li>
                </ul>
                <Button onClick={openWhatsApp} className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base font-semibold" data-testid="whatsapp-cta-modern">
                  <MessageCircle className="w-5 h-5 mr-2" /> Agendar por WhatsApp
                </Button>
              </div>
            )}
            </div>
          </div>

          {/* Mobile-only Contact Form / CTA at the bottom (order 4) */}
          <div className="lg:hidden order-4 px-4 pt-6 pb-28">
            {property.contact_method !== "whatsapp" && (
              <div className="rounded-2xl p-5 border border-gray-200 bg-gray-50 scroll-mt-24">
                <h3 className="font-bold text-gray-900 mb-1">{property.form_title || "¿Te interesa?"}</h3>
                <p className="text-sm text-gray-500 mb-4">{property.form_description || "Déjanos tus datos y te contactamos"}</p>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <Input placeholder="Tu nombre" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                  <Input type="tel" placeholder="WhatsApp" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required />
                  <Input type="email" placeholder="Email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
                  <Input placeholder="Mensaje (opcional)" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} />
                  <Button type="submit" className="w-full text-white" style={{ background: primaryColor }} disabled={submitting}>
                    {submitting ? "Enviando..." : "Enviar Mensaje"}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </div>
            )}
            {property.contact_method === "whatsapp" && (
              <div className="rounded-2xl p-6 text-white scroll-mt-24" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-lg">{property.form_title || "Agenda una visita"}</h3>
                    <p className="text-sm text-white/85 mt-0.5">{property.form_description || "Conoce esta propiedad en persona. Te respondemos al instante."}</p>
                  </div>
                </div>
                <ul className="space-y-1.5 mb-5 text-sm text-white/90">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 flex-shrink-0" /> Respuesta en minutos</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 flex-shrink-0" /> Sin compromiso</li>
                </ul>
                <Button onClick={openWhatsApp} className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base font-semibold">
                  <MessageCircle className="w-5 h-5 mr-2" /> Agendar por WhatsApp
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 z-[70] bg-black flex items-center justify-center" onClick={() => setShowGallery(false)}>
          <button onClick={() => setShowGallery(false)} className="absolute top-4 right-4 text-white/80 hover:text-white z-10">
            <X className="w-8 h-8" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setCurrentImage(i => (i - 1 + allImages.length) % allImages.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-10">
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setCurrentImage(i => (i + 1) % allImages.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-10">
            <ChevronRight className="w-10 h-10" />
          </button>
          <img
            src={getImageUrl(allImages[currentImage], { width: 1600 })}
            alt=""
            className="max-h-[90vh] max-w-[95vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 text-white/60 text-sm">{currentImage + 1} / {allImages.length}</div>
        </div>
      )}

      {/* Mobile Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-3 py-2.5 flex items-center justify-between gap-2 z-50">
        <p className="text-lg font-bold truncate min-w-0" style={{ color: primaryColor }}>{formatPrice(property.price)}</p>
        <div className="flex gap-2 flex-shrink-0">
          {property.contact_method !== "form" ? (
            <Button size="sm" onClick={openWhatsApp} className="bg-green-600 hover:bg-green-700 text-white">
              <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
            </Button>
          ) : (
            <Button size="sm" onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })} className="text-white" style={{ background: primaryColor }}>
              <Send className="w-4 h-4 mr-1" /> Contactar
            </Button>
          )}
          <Button size="sm" onClick={handleShare} variant="outline" className="px-3">
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModernaTemplate;
